<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import router from '@/router'
import IconField from 'primevue/iconfield'
import CustomInputText from '@/components/UI/CustomInputText/CustomInputText.vue'
import CustomTextarea from '@/components/UI/CustomTextarea/CustomTextarea.vue'
import InputIcon from 'primevue/inputicon'
import CustomAutocomplete from '@/features/search/components/Autocomplete.vue'
import SearchHistory from '@/features/search/components/SearchHistory.vue'
import type { RecentSearch, SuggestedBrand } from '@/types/searchItemsTypes'
import { useAutocompleteQuery } from '@/queries/search'
import { useSearch, useImageSearch, useSharedStateForComponents } from '@/features/search/store'
import { useRoute } from 'vue-router'
import CustomButton from '@/components/UI/CustomButtom/CustomButton.vue'
import DragAndDrop from '@/features/search/components/drag-and-drop/DragAndDrop.vue'
import { useI18n } from 'vue-i18n'
import { useDevice } from '@/composables/useDevice'
import TempFirstTryAddPhotoButton from '@/components/UI/CustomStrangeAddPhotoButton/TempFirstTryAddPhotoButton.vue'

const { collapsed } = defineProps<{
  collapsed?: boolean
}>()

const emit = defineEmits<{
  (e: 'updateFocus', focusedValue: boolean): void
}>()

const focusState = defineModel<boolean | undefined>()

const route = useRoute()
const searchStore = useSearch()
const searchImageStore = useImageSearch()
const { setStep2ForImageFlow } = useSharedStateForComponents()
const { t } = useI18n()
const { isDevice } = useDevice()

const { isAutofocusForSearchActive } = useSharedStateForComponents()

const searchedText = ref<string>(String(route.params.searchText ?? ''))

const autocompleteItems = ref<string[]>([])
const autocompleteBrandsItems = ref<SuggestedBrand[]>([])
const autocompleteEnabled = ref(false)
const autocompleteParams = ref({
  search_text: '',
  suggestions_size: 5,
  suggested_brands_size: 5
})
const autocompleteQuery = useAutocompleteQuery(autocompleteParams, autocompleteEnabled)

watch(
  () => autocompleteQuery.data.value,
  (data) => {
    if (!data) {
      autocompleteItems.value = []
      autocompleteBrandsItems.value = []
      return
    }
    autocompleteItems.value = data.suggestions ?? []
    autocompleteBrandsItems.value = data.suggested_brands ?? []
  }
)

const deviceType = computed(() => isDevice.value)
const croppedImageLink = computed(
  () => searchImageStore.croppedImageLink ?? searchImageStore.currentImageLink
)

type SuggestionDisplay = 'history' | 'autocomplete' | 'none'
const currentTypeOfSuggestionDisplay = computed<SuggestionDisplay>(() => {
  if (searchedText.value.length === 0 && focusState.value) {
    return 'history'
  }
  if (searchedText.value.length > 0 && focusState.value) {
    return 'autocomplete'
  }
  return 'none'
})

const isButtonLoading = computed(
  () => searchStore.isDefineFiltersLoading || searchStore.isGetSearchListLoading
)

function imageToBase64(url: string, callback: (base64: string | null) => void) {
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.src = url
  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = img.width
    canvas.height = img.height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      callback(null)
      return
    }
    ctx.drawImage(img, 0, 0)
    const base64 = canvas.toDataURL('image/png')
    callback(base64)
  }
  img.onerror = () => {
    console.error('Не удалось загрузить изображение')
    callback(null)
  }
}

function onFocus() {
  focusState.value = true
}

function onBlur() {
  setTimeout(() => {
    focusState.value = false
  }, 500)
}

async function startSearchFlow() {
  await router.push({
    path: `/searchList/${searchedText.value}`,
    query: {
      ...route.query,
      image: croppedImageLink.value ?? undefined
    }
  })
}

function setTextFromSuggestionToTextArea(text: string) {
  searchedText.value = text
}

function clearInput() {
  searchedText.value = ''
}

async function setTextFromSearchHistory(item: RecentSearch) {
  searchedText.value = item.query.text ?? ''
  const historyImage = item.query.image
  if (historyImage) {
    imageToBase64(historyImage, (base64) => {
      if (base64) {
        searchImageStore.imageBase64Data = base64
        const urlObj = new URL(historyImage)
        const pathParts = urlObj.pathname.split('/')
        searchImageStore.croppedImageLink = pathParts[pathParts.length - 1] ?? ''
      }
    })
    setStep2ForImageFlow()
  }
}

async function getAutocomplete() {
  const text = searchedText.value.trim()
  if (!text) {
    autocompleteItems.value = []
    autocompleteBrandsItems.value = []
    autocompleteEnabled.value = false
    return
  }

  // Обновляем параметры и enabled одновременно
  // Это гарантирует, что и key, и enabled обновятся в одном тике
  autocompleteParams.value = {
    search_text: text,
    suggestions_size: 5,
    suggested_brands_size: 5
  }
  autocompleteEnabled.value = true
  
  // Ждем, пока Vue обработает оба обновления и watch обновит lastKey
  await nextTick()
  
  // Принудительно обновляем запрос с новым ключом
  // Это гарантирует выполнение запроса даже если watch не сработал
  await autocompleteQuery.refresh(true)
}
</script>

<template>
  <div
    class="search__wrapper"
  >
    <template v-if="!collapsed">
      <IconField :pt="{ root: { style: { width: '100%', borderRadius:'40px' } } }">
        <custom-input-text
          v-model="searchedText"
          @input="getAutocomplete"
          @blur="onBlur"
          @focus="onFocus"
          @keydown.enter="startSearchFlow"
          :placeholder="deviceType === 'mobile' ? t('home_page.search'): t('home_page.search_placeholder')"
          :rounded="true"
          class="full-width custom-heart"
        />
        <InputIcon v-if="searchedText.length > 0" class="pi pi-times position-icon" @click="clearInput" />
      </IconField>

      <search-history
        v-if="currentTypeOfSuggestionDisplay === 'history'"
        @set-text-from-search-history-to-text-area="setTextFromSearchHistory"
      />

      <CustomAutocomplete
        v-if="currentTypeOfSuggestionDisplay === 'autocomplete'"
        :autocomplete-brands-items="autocompleteBrandsItems"
        :autocomplete-items="autocompleteItems"
        @set-text-from-search-history-to-text-area="setTextFromSuggestionToTextArea"
      />

      <temp-first-try-add-photo-button :image-url="searchImageStore.imageBase64Data ?? undefined"
                                        :disable-search="searchedText.length === 0"
                                       @search="startSearchFlow"
      />
    </template>
    <template v-else>

      <div class="custom-input-and-buttons-wrapper" >
        <custom-input-text
          v-model="searchedText"
          class="full-width custom-input-height"
          @input="getAutocomplete"
          @blur="onBlur"
          @focus="onFocus"
          :autofocus="isAutofocusForSearchActive"
          :rounded="true"
          :placeholder="'Search'"
        />

        <DragAndDrop />

        <custom-button
            :label=" isDevice === 'mobile' ? '' : t('home_page.search')"
            :loading="isButtonLoading"
            :pt="{root:{style: { minWidth: '40px' }}}"
            :disabled="searchedText.length === 0"
            loading-icon="pi pi-spinner"
            @click="startSearchFlow"
            rounded
        />


      </div>
      <search-history
        v-if="currentTypeOfSuggestionDisplay === 'history'"
        @set-text-from-search-history-to-text-area="setTextFromSearchHistory"
      />

      <CustomAutocomplete
        v-if="currentTypeOfSuggestionDisplay === 'autocomplete'"
        :autocomplete-brands-items="autocompleteBrandsItems"
        :autocomplete-items="autocompleteItems"
        @set-text-from-search-history-to-text-area="setTextFromSuggestionToTextArea"
      />
    </template>
  </div>

</template>

<style scoped>

.typing-placeholder {
  display: flex;
  align-items: center;
  gap:8px;
  left: 30px;
  height: 80%;
  top: 0;
  width: fit-content;
  position: absolute;
  color:var(--primary-blue-40);
}

.search__wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 7;
  padding: 8px;
  gap:8px;
  background: var(--white-100);
  border: 2px solid #FFF;
  border-radius: 36px;
  transition: background 0.3s ease;
  box-shadow: 0px 14px 42px -10px rgba(186, 174, 206, 0.50);
}

.search-autocomplete-text-tip li {
  padding: 8px 12px;
  background: white;
  border-radius: 28px;
  cursor: pointer;
}

.search-history-text-tip li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border-radius: 28px;
  cursor: pointer;
}

.search-history-text-tip li:active {
  background: rgba(0, 0, 0, 0.1);
}

.custom-input-and-buttons-wrapper {
  display: flex;
  width: 100%;
  align-items: center;
  gap:8px;
}

.custom-input-height {
  height:46px;
}

.search__wrapper:has(.custom-heart:focus) {
  background:var(--white-100);
}


.typing-placeholder {
  overflow: hidden; /* Ensures the content is not revealed until the animation */
  border-right: .15em solid var(--primary-blue-12); /* The typwriter cursor */
  white-space: nowrap; /* Keeps the content on a single line */
  margin: 0 auto; /* Gives that scrolling effect as the typing happens */
  border-color:transparent;
  animation: typing 3.5s steps(40, end)
}

.custom-heart {
  height:60px;
  padding-left:20px;
}

.position-icon {
  right:20px !important;
  cursor: pointer;
}

.position-icon:hover {
  color:var(--primary-blue-100);
}

/* The typing effect */
@keyframes typing {
  from { width: 0 }
  to { width: 80% }
}

/* The typewriter cursor effect */


@media screen and (max-width: 1440px) {
  .search__wrapper {
    width:74%;
  }
}

@media screen and (max-width: 1366px) {
  .search__wrapper {
    width:81%;
  }
}

@media screen and (max-width: 1024px) {
  .search__wrapper {
    position: absolute;
    width:81%;
    top:24%;
  }
}

@media screen and (max-width: 866px) {
  .search__wrapper {
    width:92%;
  }


}
</style>
